import { useMemo } from 'react';
import { useTenantStore } from '../../shared/stores/useTenantStore';
import { useAuthStore } from '../auth/store';
import { useMerchant } from '../merchants/hooks/useMerchants';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import { useMerchantSettlements, useOwnSettlements } from '../settlements/hooks/useSettlements';
import { MerchantPayoutDestination } from '../payout-destinations/MerchantPayoutDestination';
import { EarningsSummary } from './EarningsSummary';
import { SettlementHistoryTable } from './SettlementHistoryTable';
import { computeEarnings } from './earnings';

export default function Payouts() {
  // A TCS admin's picked merchant lives in useTenantStore.activeMerchant. A merchant
  // admin's own tenant lives in useAuthStore.selectedTenantId (set from their JWT at
  // login — activeMerchant is never populated for that flow since the tenant-switcher
  // UI, the only thing that writes it, is TCS-admin-only). Fall back to selectedTenantId
  // so merchant-role users can actually reach their own Payouts dashboard (PE-2107).
  const activeMerchant = useTenantStore((s) => s.activeMerchant);
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const roleScope = useAuthStore((s) => s.user?.roleScope);
  const { hasPermission } = usePermissions();

  // Merchant-role users read their OWN settlements via the MERCHANT-scoped
  // /merchants/me/payouts endpoint (tenant derived from their JWT). The
  // /settlements/merchant/:id endpoint a TCS admin uses is COLLECTIVE-only and
  // 403s for merchants — so we must branch by scope, not just fall back on the id.
  const isMerchantScoped = roleScope === 'MERCHANT';

  const tenantId = activeMerchant?.id ?? selectedTenantId ?? null;
  const { data: fallbackMerchant } = useMerchant(!activeMerchant && tenantId ? tenantId : '');
  const businessName = activeMerchant?.name ?? fallbackMerchant?.name;

  if (!hasPermission(Permission.SETTLEMENT_VIEW)) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <p className="text-sm text-[#3F3E42]">You don&apos;t have permission to view payouts.</p>
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <p className="text-sm text-[#3F3E42]">Select a merchant to view payouts.</p>
      </div>
    );
  }

  return (
    <PayoutsInner
      tenantId={tenantId}
      businessName={businessName}
      isMerchantScoped={isMerchantScoped}
    />
  );
}

interface InnerProps {
  tenantId: string;
  businessName?: string;
  isMerchantScoped: boolean;
}

function PayoutsInner({ tenantId, businessName, isMerchantScoped }: InnerProps) {
  // Exactly one of these fires (the other is disabled), keyed off role scope.
  const own = useOwnSettlements(isMerchantScoped);
  const byMerchant = useMerchantSettlements(isMerchantScoped ? '' : tenantId);
  const { data: settlements = [], isLoading, error } = isMerchantScoped ? own : byMerchant;

  const summary = useMemo(() => computeEarnings(settlements), [settlements]);

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-[#28282A]">Payouts</h1>

      {isLoading && <p className="text-sm text-[#3F3E42]">Loading payouts…</p>}
      {error && <p className="text-sm text-red-500">Failed to load payouts.</p>}

      {!isLoading && !error && (
        <>
          <EarningsSummary summary={summary} />
          <MerchantPayoutDestination tenantId={tenantId} businessName={businessName} maskAccount />
          <SettlementHistoryTable
            settlements={settlements}
            isMerchantScoped={isMerchantScoped}
            tenantId={tenantId}
          />
        </>
      )}
    </div>
  );
}
